import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useBindEcard, useBindingEcards, useCancelBindingEcards, useEcards } from '../../services/hooks'
import { showToast } from '../common/tools'
import {
  isFaceEquippable,
  isFaceSelectionApplied,
  isSelfServiceBindFace,
  resolveEcardFaceSelection,
} from '../common/utils'
import { changeEcardActiveFalse, changeEcardActiveTrue, refreshEcards } from '../../actions'
import styles from './CertShowWrapper.module.css'

const CardStatusBtn = ({ hasChangeRight, cert, imgItem, updateResult }) => {
  const dispatch = useDispatch()
  const { push } = useHistory()
  // 防重复提交：装备/取消装备 + 回读期间不再响应点击
  const [submitting, setSubmitting] = useState(false)

  // 换面成功后回读权威证书列表（GET /api/ecards）：active 由权威响应给出，不再只靠本地按响应回填——
  // PUT /rebinding 只回显请求（后端对不可得面静默跳过），响应里的 ecardImageId 不代表真的装备上了。
  const { runAsync: reloadEcards } = useEcards({
    manual: true,
    onSuccess: (data) => dispatch(refreshEcards(data)),
  })

  const { runAsync: rebindEcards } = useBindingEcards({})
  const { runAsync: cancelBindEcards } = useCancelBindingEcards({})
  const { runAsync: bindFace } = useBindEcard({})

  const ecardId = cert.affiliateCertificationId || cert.affiliateCredentialId

  // 回读权威证书列表，判期望的展示位是否已生效（口径见 isFaceSelectionApplied）
  const confirmApplied = async (item) => {
    try {
      const latest = await reloadEcards()
      const certs = [...((latest && latest.certifications) || []), ...((latest && latest.credentials) || [])]
      const target = certs.find((c) => (c.affiliateCertificationId || c.affiliateCredentialId) === ecardId)
      return isFaceSelectionApplied(target, {
        ecardImageId: item.ecardImageId != null ? item.ecardImageId : item.ecardImgId,
        bindingId: item.bindingId,
      })
    } catch (e) {
      return false
    }
  }

  const equipmentCard = async () => {
    if (submitting) return
    // 端点与载荷由纯函数给出：无绑定行的换面集面走 POST /binding（PUT /rebinding 会被后端静默跳过）、
    // 普通版走 cancelBinding、其余走 rebinding。是否真有权益由后端判（非 VIP 回 club.vip.need，让它报错）。
    const selection = resolveEcardFaceSelection({
      ecardId,
      face: imgItem,
      customBindingId: imgItem.id,
      plastic: imgItem.desc === '普通版',
    })
    if (!selection) return
    const [item] = selection.payload
    setSubmitting(true)
    try {
      let result
      if (selection.endpoint === 'binding') result = await bindFace(selection.payload)
      else if (selection.endpoint === 'rebinding') result = await rebindEcards(selection.payload)
      else result = await cancelBindEcards(selection.payload)
      // 回读权威状态再决定是否提示成功：不能只凭响应（rebinding 只回显请求，可能被静默跳过）
      const applied = await confirmApplied(item)
      if (!applied) {
        showToast('卡面更换失败，请稍后重试', false)
        return
      }
      showToast('卡面更换成功！')
      if (selection.endpoint === 'cancelBinding') dispatch(changeEcardActiveFalse(result))
      else dispatch(changeEcardActiveTrue(result))
      updateResult(result)
    } catch (e) {
      // 权益不足（如非 VIP 的 club.vip.need）等错误已由 useRequestCommon 弹窗提示，这里不弹成功
    } finally {
      setSubmitting(false)
    }
  }

  const toBuyCards = () => {
    push('/buy_cards', { ecardId: cert.encodedId, cardImageName: imgItem.desc })
  }

  // isFaceEquippable 为真可能是「已拥有」（ecardImageBindingId / 自定义 id / 普通版），也可能只是
  // 「VIP 期内可用」（vipAvailable，无绑定行、并不拥有）。后者不能写「您已拥有该卡」。
  const equipText = () => {
    const owned = !!(imgItem.ecardImageBindingId || imgItem.id || imgItem.desc === '普通版')
    return owned ? '您已拥有该卡，点击装备该卡面' : 'VIP 期内可装备该卡面，点击装备'
  }

  if (!imgItem) return null

  if (imgItem.active) {
    return (
      <>
        <div className={styles['status-btn']}>
          <img
            src={require('../../assets/images/buy_cards/equipped-icon.png')}
            alt="vip-icon"
            className={styles.icon}
          />
          <div className={styles.text}>您已装备该卡面</div>
        </div>
      </>
    )
  } else {
    if (imgItem.yearImage) {
      if (hasChangeRight || isFaceEquippable(imgItem)) {
        return (
          <>
            <div className={styles['status-btn']} onClick={equipmentCard}>
              <div className={styles.text}>恭喜，您符合生肖卡领取规则，可免费装备该卡面</div>
            </div>
          </>
        )
      } else {
        if (imgItem.currentYearImage) {
          return (
            <>
              <div className={styles['status-btn']} onClick={() => push('/newYearCampaignPost')}>
                <div className={styles.text}>恭喜，您符合生肖卡领取规则，点击领取该卡面</div>
              </div>
            </>
          )
        } else {
          return (
            <>
              <div className={styles['status-btn']} onClick={toBuyCards}>
                <div className={styles.text}>您暂未获得该卡，点击解锁该卡面</div>
              </div>
            </>
          )
        }
      }
    } else if (imgItem.desc.indexOf('周年') >= 0) {
      if (hasChangeRight || isFaceEquippable(imgItem)) {
        return (
          <>
            <div className={styles['status-btn']} onClick={equipmentCard}>
              <div className={styles.text}>{equipText()}</div>
            </div>
          </>
        )
      } else {
        return (
          <>
            <div className={styles['status-btn']} onClick={toBuyCards}>
              <div className={styles.text}>您暂未获得该卡，点击解锁该卡面</div>
            </div>
          </>
        )
      }
    } else {
      if (imgItem.id || isFaceEquippable(imgItem) || imgItem.desc === '普通版' || isSelfServiceBindFace(imgItem)) {
        return (
          <>
            <div className={styles['status-btn']} onClick={equipmentCard}>
              <div className={styles.text}>{equipText()}</div>
            </div>
          </>
        )
      } else {
        return (
          <>
            <div className={styles['status-btn']} onClick={toBuyCards}>
              <div className={styles.text}>您暂未获得该卡，点击解锁该卡面</div>
            </div>
          </>
        )
      }
    }
  }
}

export default CardStatusBtn
